'use client'

import { useEffect, useRef, useState } from 'react'

import Link from '@/components/client/seo/link'
import { ScoreBadgePopover } from '@/components/client/storyblok/article/score-badge-popover'
import { ImageWrapper } from '@/components/image'
import type { SidebarProduct } from '@/lib/sidebar'
// Deep import, not the `@/lib/sidebar` barrel: this is a client component, and the
// barrel re-exports the `'use cache'` DB loader. (The type import above is erased
// at compile time, so it carries nothing into the bundle.)
import { SIDEBAR_PRODUCT_COUNT } from '@/lib/sidebar/constants'
import { stickyShuffle } from '@/lib/sticky-shuffle'

import { FeaturedProductsHeader } from './featured-products-header'
import { FeaturedProductSkeleton } from './featured-products-skeleton'

// Labels are resolved server-side (via context.t in the Sidebar) and passed in,
// so this client component stays free of dictionary wiring and English literals.
export interface FeaturedProductsLabels {
  featuredProducts: string
  compareAll: string
  staffPick: string
  viewProduct: string
  featured: string
}

interface FeaturedProductsProps {
  products: SidebarProduct[]
  labels: FeaturedProductsLabels
  headerClassName?: string
  /**
   * Scopes the viewer's sticky order. The rail is handed two different pools —
   * the shared featured rotation, or one brand's products — and they must not
   * share a seed, or switching between them would look like a reshuffle.
   */
  orderKey: string
}

// PostHog event names — the engine appends `--click` / `--impression`. These
// match the old site's two-event scheme exactly (a distinct event for the
// staff-pick card vs the list cards) so existing dashboards/funnels carry over.
const FEATURED_EVENT = 'sidebar-product-featured'
const LIST_EVENT = 'sidebar-product'

// The old site sent this as a top-level `origin` on every sidebar event. The
// new engine only captures `data-event-*`, so it lands as an `eventOrigin`
// property — same value, carried over for dashboard continuity.
const EVENT_ORIGIN = 'article/featured-products'

const formatPrice = (price: number | null, priceType: string | null) => {
  if (price == null) {
    return null
  }
  const formatted = `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
  if (priceType && /pair/.test(priceType)) {
    return `${formatted} / pair`
  }
  if (priceType && /month/.test(priceType)) {
    return `${formatted} / mo`
  }
  return formatted
}

// `storySlug` is the product story's *full* slug — it already carries the
// section folder (`hearing-aids/oticon-intent`, and for a few products
// `cochlear-implants/…` / `bone-anchored-hearing-aids/…`). Prepending a section
// here doubles it, so the path is the slug verbatim.
const productPath = (product: SidebarProduct) => `/${product.storySlug}`

// Delegated PostHog tracking: the engine walks up to the nearest `data-track`
// ancestor, so these live on the card container (not the stretched link) to
// catch clicks anywhere on the card. `data-event-url` supplies the link prop
// since the container isn't an anchor. Cards only render post-shuffle (and never
// in the editor), so tracking is always live when a card exists.
const trackingProps = (eventName: string, product: SidebarProduct): Record<string, string> => ({
  'data-track': '',
  'data-track-impression': '',
  'data-event-name': eventName,
  'data-event-release': product.slug,
  'data-event-origin': EVENT_ORIGIN,
  'data-event-url': productPath(product),
})

const FeaturedCard = ({
  product,
  staffPick,
  viewProduct,
}: {
  product: SidebarProduct
  staffPick: string
  viewProduct: string
}) => {
  const priceText = formatPrice(product.price, product.priceType)

  return (
    <div
      className="group relative cursor-pointer rounded-2xl bg-[#142546] shadow-lg transition-all hover:-translate-y-1"
      {...trackingProps(FEATURED_EVENT, product)}
    >
      {/* Stretched link. `z-10` puts it above the image, the gradient overlays and
          the `z-[1]` caption block — at `z-0` those all painted on top of it and
          swallowed the hover/click, so the card looked clickable but wasn't. The
          score badge sits at `z-20` and stays above it. */}
      <Link
        href={productPath(product)}
        prefetch={false}
        data-tracking="ignore"
        className="absolute inset-0 z-10 rounded-2xl"
        aria-label={product.name}
        tabIndex={-1}
      />
      {/* The clip lives here, not on the card: it exists to round off the image
          and its gradients. On the card it also swallowed the score popover. */}
      <div className="relative aspect-[16/10] w-full overflow-hidden rounded-t-2xl">
        <ImageWrapper
          image={product.imageUrl}
          origin="sidebar-featured-product"
          loadTime="lazy"
          imgProps={{
            alt: product.name,
            className: 'absolute inset-0 h-full w-full object-cover',
          }}
          sizingProps={{ sizes: '(min-width: 992px) 400px, 100vw' }}
        />
        <div className="absolute inset-0 bg-[#142546]/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#142546] via-transparent to-transparent" />
      </div>
      {/* Outside the image wrapper so the popover escapes its clip. Anchored to
          the card, which starts at the same top edge — so it doesn't move. */}
      {product.score != null && product.score > 0 && (
        <div className="absolute top-3 right-3 z-20">
          <ScoreBadgePopover score={product.score} variant="solid" placement="bottom" />
        </div>
      )}
      <div className="relative z-[1] -mt-12 p-5">
        <div className="mb-2 flex items-center gap-2">
          <span className="rounded bg-[#4a7fe5] px-2 py-0.5 text-[10px] font-bold tracking-widest text-white uppercase shadow-sm">
            {staffPick}
          </span>
        </div>
        <span className="mb-2 block text-xl leading-tight font-bold text-white">{product.name}</span>
        <div className="mt-1 flex items-center justify-between border-t border-white/10 pt-3">
          {priceText ? (
            <span className="text-lg font-bold text-blue-400">{priceText}</span>
          ) : (
            <span className="text-sm font-medium text-white/70">{viewProduct}</span>
          )}
          <svg
            className="h-5 w-5 text-white/50 transition-colors group-hover:text-white"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12h14" />
            <path d="m12 5 7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  )
}

const ProductListCard = ({ product, featured }: { product: SidebarProduct; featured: string }) => {
  const priceText = formatPrice(product.price, product.priceType)

  return (
    <div
      className="group relative flex cursor-pointer gap-4 rounded-xl border border-slate-100 bg-white p-4 shadow-sm transition-all hover:border-[#4a7fe5]/30 hover:shadow-md"
      {...trackingProps(LIST_EVENT, product)}
    >
      {/* Stretched link at `z-10`. The two columns below are deliberately left at
          `z-auto`: as bare `relative` they don't open a stacking context, so the
          score badge nested inside can still escape to `z-20` and stay clickable
          above this link. Giving them `z-[1]` would trap the badge under it. */}
      <Link
        href={productPath(product)}
        prefetch={false}
        data-tracking="ignore"
        className="absolute inset-0 z-10 rounded-xl"
        aria-label={product.name}
        tabIndex={-1}
      />
      <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-lg bg-slate-100">
        <ImageWrapper
          image={product.imageUrl}
          origin="sidebar-product-list"
          loadTime="lazy"
          imgProps={{
            alt: product.name,
            className: 'h-full w-full object-cover group-hover:scale-105 transition-transform duration-500',
          }}
          sizingProps={{ fixed: true, fixedWidth: 96, fixedHeight: 96 }}
        />
      </div>
      <div className="relative flex min-w-0 flex-1 flex-col">
        <span className="mb-1 inline-block self-start rounded bg-[#4a7fe5]/10 px-1.5 py-0.5 text-[9px] font-bold tracking-widest text-[#4a7fe5] uppercase">
          {featured}
        </span>
        <div className="flex flex-1 flex-col justify-between">
          <div>
            <span className="block text-[17px] leading-tight font-bold text-[#142546] transition-colors group-hover:text-[#4a7fe5]">
              {product.name}
            </span>
            {priceText && <span className="mb-1 block text-sm font-semibold text-slate-500">{priceText}</span>}
          </div>
          {product.score != null && product.score > 0 && (
            <div className="relative z-20 mt-auto flex items-end justify-end">
              <ScoreBadgePopover score={product.score} variant="default" />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export const FeaturedProducts = ({ products, labels, orderKey, headerClassName }: FeaturedProductsProps) => {
  // `products` is the full rotation pool (every curated featured product), not a
  // shortlist: we order it and slice to SIDEBAR_PRODUCT_COUNT here, so each pick
  // gets a turn both at appearing at all and at being the staff-pick card. The
  // server can't do either — the article page is statically prerendered, so a
  // server-side shuffle would freeze one order for every visitor — and it can't
  // run during render/SSR without a hydration mismatch.
  //
  // The order is random per VIEWER but fixed for that viewer (stickyShuffle,
  // 24h seed) — not re-rolled per mount. A plain shuffle here dealt a new hand
  // on every article and every page load, so the rail (and the staff-pick card)
  // churned as you read down the site. Ranking by a seeded hash of the release
  // id also survives the pool changing between articles: each page drops its own
  // product from the pool, and that must not reorder the rest.
  //
  // So until the shuffle is applied (`ready`) we render skeletons ONLY — never
  // the real cards in their pre-shuffle (pool) order. The visitor therefore
  // only ever sees: skeletons → the final randomized cards, and never watches
  // the staff-pick swap. (Rendering the real cards hidden-but-present still let
  // their unshuffled order paint for a frame in dev — StrictMode + a post-paint
  // effect — which is the flash this avoids.) In the Storyblok editor we never
  // flip `ready`, so skeletons stay put and editors don't see live sponsored cards.
  const [ordered, setOrdered] = useState<SidebarProduct[]>([])
  const [ready, setReady] = useState(false)
  const done = useRef(false)

  useEffect(() => {
    if (done.current) {
      return
    }
    done.current = true
    const editor = /_storyblok|storyblok=/.test(window.location.search)
    if (editor) {
      return
    }
    setOrdered(stickyShuffle(products, (product) => String(product.id), orderKey).slice(0, SIDEBAR_PRODUCT_COUNT))
    setReady(true)
  }, [products, orderKey])

  if (!products.length) {
    return null
  }

  // Card count, so the skeletons match the eventual layout even though the pool
  // they stand in for is larger.
  const visibleCount = Math.min(products.length, SIDEBAR_PRODUCT_COUNT)
  const [featured, ...rest] = ordered

  return (
    <div className="flex flex-col gap-5">
      <FeaturedProductsHeader
        title={labels.featuredProducts}
        compareAll={labels.compareAll}
        className={headerClassName}
      />

      {/* Staff Pick card — skeleton until the shuffle is applied */}
      {ready && featured ? (
        <FeaturedCard product={featured} staffPick={labels.staffPick} viewProduct={labels.viewProduct} />
      ) : (
        <FeaturedProductSkeleton variant="featured" />
      )}

      {/* List cards */}
      {visibleCount > 1 && (
        <div className="flex flex-col gap-4">
          {ready
            ? rest.map((product) => <ProductListCard key={product.id} product={product} featured={labels.featured} />)
            : Array.from({ length: visibleCount - 1 }, (_, i) => <FeaturedProductSkeleton key={i} variant="list" />)}
        </div>
      )}
    </div>
  )
}
